<script>
    import { onMount } from "svelte";
    import Spinner from "../components/Spinner.svelte";
	import { user, token } from '../stores.js';
	import { apiPost } from '../api.js';

	user.useLocalStorage();

	const base_url = window.location.origin;
	const url = "SECURESHARE_API_URL";
	const api_key = "SECURESHARE_API_KEY";

	const ID_DISPLAY_LENGTH = 15;

	function truncateId(id) {
		return id.length > ID_DISPLAY_LENGTH ? id.slice(0, ID_DISPLAY_LENGTH) + '…' : id;
	}

    let links = $state([]);
    let loading = $state(true);
    let error = $state(null);

	let openRequests = $derived(links.filter(l => !l.secret_id));
	let responses = $derived(links.filter(l => l.secret_id));

    async function requestMyGuestLinks() {
		const res = await apiPost(`${url}/retrieve_my_guest_links`, {}, { token: $token, apiKey: api_key });
		const json = await res.json();
		if (res.ok) {
			links = json.links;
		} else {
			error = "Failed to load requests.";
		}
		loading = false;
	}

	async function deleteLink(id) {
		if (!confirm("Delete this request? This cannot be undone.")) return;

		const res = await apiPost(`${url}/delete_guest_link`, { id: id }, { token: $token, apiKey: api_key });
		if (res.ok) {
			links = links.filter(l => l.id !== id);
		}
	}

	function formatExpiry(ttl) {
		const date = new Date(ttl * 1000);
		return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
	}

	function isExpired(ttl) {
		return Date.now() > ttl * 1000;
	}

	function guestLinkUrl(id) {
		return `${base_url}/respond/${id}`;
	}

	let copiedId = $state(null);
	async function copyLink(id) {
		await navigator.clipboard.writeText(guestLinkUrl(id));
		copiedId = id;
		setTimeout(() => { if (copiedId === id) copiedId = null; }, 2000);
	}

	// See MySecrets.svelte for the rationale — same shape: re-fire on
	// every $token change so the table reloads when Home is remounted
	// after a nav back, and so we naturally pick up the token Login.svelte
	// fetches asynchronously on first load.
	$effect(() => {
		if ($token) requestMyGuestLinks();
	});
</script>

{#if loading}
	<Spinner></Spinner>
{:else if error}
	<p class="alert alert-danger">{error}</p>
{:else if links.length === 0}
	<p class="text-muted">No requests yet.</p>
{:else}
	<h3>Open Requests</h3>
	{#if openRequests.length === 0}
		<p class="text-muted">No open requests.</p>
	{:else}
		<table class="table">
			<thead>
				<tr>
					<th>ID</th>
					<th>Memo</th>
					<th>Internal Note</th>
					<th>Expires</th>
					<th class="actions-col"></th>
				</tr>
			</thead>
			<tbody>
			{#each openRequests as link}
				<tr class:expired={isExpired(link.TTL)}>
					<td class="id-cell">
						<span class="id-text" title={link.id}>{truncateId(link.id)}</span>
						<button class="copy-btn" onclick={() => copyLink(link.id)} title="Copy link to clipboard">
							{#if copiedId === link.id}
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>
							{:else}
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/><path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/></svg>
							{/if}
						</button>
					</td>
					<td class="memo-cell" title={link.memo || ''}>{link.memo || ''}</td>
					<td>{link.note || ''}</td>
					<td>{formatExpiry(link.TTL)}</td>
					<td class="actions">
						<button onclick={() => deleteLink(link.id)} class="btn btn-sm btn-outline-danger">Delete</button>
					</td>
				</tr>
			{/each}
			</tbody>
		</table>
	{/if}

	<h3>Responses</h3>
	{#if responses.length === 0}
		<p class="text-muted">No responses yet.</p>
	{:else}
		<table class="table">
			<thead>
				<tr>
					<th>Memo</th>
					<th>Internal Note</th>
					<th>Ref</th>
					<th>Expires</th>
					<th>Reads</th>
					<th class="actions-col"></th>
				</tr>
			</thead>
			<tbody>
			{#each responses as link}
				<tr class:expired={link.secret_TTL && isExpired(link.secret_TTL)}>
					<td class="memo-cell" title={link.memo || ''}>{link.memo || ''}</td>
					<td>{link.note || ''}</td>
					<td>{link.secret_id.substring(0, 10)}</td>
					<td>{formatExpiry(link.secret_TTL)}</td>
					<td>{link.secret_reads} / {link.secret_burn_after_reading}</td>
					<td class="actions">
						{#if link.secret_available}
							<a href="{base_url}/read/{link.secret_id}" class="btn btn-sm btn-outline-secondary">Read Response</a>
						{:else}
							<span class="tooltip-wrapper" title={link.secret_unavailable_reason}><span class="btn btn-sm btn-outline-secondary disabled">Read Response</span></span>
						{/if}
						<button onclick={() => deleteLink(link.id)} class="btn btn-sm btn-outline-danger">Delete</button>
					</td>
				</tr>
			{/each}
			</tbody>
		</table>
	{/if}
{/if}

<style>
	.expired td {
		opacity: 0.5;
	}

	.expired td a:not(.btn) {
		text-decoration: line-through;
	}

	table {
		font-size: 14px;
	}

	th, td {
		padding: 6px 10px;
		vertical-align: middle;
	}

	.id-cell {
		white-space: nowrap;
	}

	.id-text {
		font-family: monospace;
		font-size: 13px;
	}

	.memo-cell {
		max-width: 240px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.copy-btn {
		background: none;
		border: none;
		padding: 0 .25rem;
		cursor: pointer;
		color: #6c757d;
		vertical-align: middle;
	}

	.copy-btn:hover {
		color: #0d6efd;
	}

	.link-expiry {
		font-size: 12px;
		color: #6c757d;
	}

	.tooltip-wrapper {
		display: inline-block;
		cursor: not-allowed;
	}

	.tooltip-wrapper > .disabled {
		pointer-events: none;
	}

	.actions-col, .actions {
		text-align: right;
	}

	.actions {
		white-space: nowrap;
		display: flex;
		justify-content: flex-end;
		align-items: stretch;
		gap: .25rem;
	}

	.actions :global(a.btn) {
		display: flex;
		align-items: center;
	}

	td :global(.btn) {
		padding: .25rem .5rem;
		font-size: .875rem;
		line-height: 1.5;
	}
</style>
